import { useState } from 'react';
import { motion } from 'motion/react';
/* `public/` yerine içe aktarma: `vite.config.ts` `base: './'` kullanıyor
   (Capacitor dosyayı `file://` üzerinden açıyor), orada `/mascot-head.png`
   gibi mutlak bir yol çözülmez. İçe aktarınca yolu Vite yazıyor ve dosya
   ayrıca içerik özetiyle adlandırılıp önbelleğe alınabiliyor. */
import headUrl from '../assets/mascot-head.png';

/**
 * Maskot: elinde top sektirerek masayı dolaşan kafa.
 *
 * Soldan girer, dört oyuncunun yanında sırayla duraklar, sonra geldiği taraftan
 * çıkar. Dört ayrı hareket üst üste biniyor — bir arada "canlı" görünmesinin
 * sebebi bu:
 *
 *   1. TUR (burada, Motion ile) — duraktan durağa konum.
 *   2. ÖLÇEK (yine Motion, ayrı düğümde) — uzaktayken küçük, yakınken büyük.
 *   3. SEKME + BAŞ SALINIMI (CSS keyframes) — turdan bağımsız, kendi
 *      döngüsünde. Ayrı elemanlarda tutuluyor çünkü hepsi `transform`
 *      yazıyor; aynı düğümde olsalar biri diğerini ezerdi.
 *   4. GÖLGE — top yere yaklaştıkça küçülüp koyulaşır. Yükseklik hissini
 *      veren asıl şey topun kendisi değil, bu.
 *
 * Kafa görseli `src/assets/mascot-head.png`. Yüklenemezse yerine bir taslak
 * geçiyor — animasyonun ölçüsü bozulmasın, kırık ikon görünmesin.
 */

/**
 * Tur durakları — masanın dört oyuncusunu dolaşıp geldiği yerden çıkış.
 *
 * `x` maskotun SOL kenarının masa genişliğine oranı, `y` yer hizasının masa
 * DİBİNDEN yüksekliği (eksi işareti yukarı demek). İkisi de `.mascot__walk`
 * masayla birebir olduğu için doğrudan masanın oranı.
 *
 * Koordinatlar tahminle değil ölçülerek seçildi. Masanın orta sütunu baştan
 * aşağı dolu: üst plaka ile atılan kartların yığını arasında maskotun boyunun
 * sığacağı boşluk YOK (masaüstünde o aralık 99 px, maskot 161 px). Bu yüzden
 * duraklar ortadan kaçıp boş çuhanın dört köşesine oturuyor; üç ekran ölçüsünde
 * de (masaüstü, dikey ve yatay telefon) yüzün üstüne hiçbir şey gelmiyor.
 *
 * `scale` derinlik için: masanın karşı tarafındayken küçük, kendi tarafımıza
 * indiğinde büyük. Turu "yan yana kayma" olmaktan çıkarıp "etrafında dolaşma"
 * yapan asıl şey bu.
 */
const STOPS = {
  //          x        y (yerden)  ölçek
  giris:    ['-22%',  '-56%',      0.92],
  sol:      [ '12%',  '-58%',      0.95],
  ust:      [ '30%',  '-62%',      0.86],
  sag:      [ '68%',  '-58%',      0.95],
  ben:      [ '26%',  '-22%',      1.14],
  donus:    [ '12%',  '-48%',      1.0 ],
  cikis:    ['-25%',  '-54%',      0.92],
} as const;

/** Sırayla gidilen duraklar; her birinde kısa bir duraklama var. */
const ROUTE = ['giris', 'sol', 'sol', 'ust', 'ust', 'sag', 'sag', 'ben', 'ben', 'donus', 'cikis'] as const;

/**
 * Durakların zaman çizgisindeki yerleri (0-1). Ardışık aynı durak = duraklama.
 * Kendi tarafımıza inen bacak bilerek uzun: masayı çaprazlama geçiyor.
 */
const TIMES = [0, 0.11, 0.19, 0.30, 0.38, 0.49, 0.57, 0.70, 0.79, 0.90, 1];

/** Turun toplam süresi (ms). Kuyruk beklemiyor — üstte oynayıp gidiyor.
 *  Sektirme temposu CSS'te (`--dribble`); tek kaynak orası. */
export const MASCOT_MS = 5600;

const track = (i: 0 | 1 | 2) => ROUTE.map((stop) => STOPS[stop][i]);
/* Duraklamalar dışında her bacak yumuşak girip yumuşak çıkıyor. */
const EASE = TIMES.slice(1).map(() => 'easeInOut' as const);

export function Mascot({ onDone }: { onDone(): void }) {
  const [broken, setBroken] = useState(false);

  return (
    <div className="mascot" aria-hidden>
      <motion.div
        className="mascot__walk"
        initial={{ x: STOPS.giris[0], y: STOPS.giris[1] }}
        animate={{ x: track(0), y: track(1) }}
        transition={{ duration: MASCOT_MS / 1000, times: TIMES, ease: EASE }}
        onAnimationComplete={onDone}
      >
        {/* Ölçek AYRI düğümde: konum `.mascot__walk` üzerinde ve o kutu masa
            boyutunda — orada ölçeklenseydi koordinat sistemi de büyürdü. */}
        <motion.div
          className="mascot__char"
          initial={{ scale: STOPS.giris[2] }}
          animate={{ scale: track(2) }}
          transition={{ duration: MASCOT_MS / 1000, times: TIMES, ease: EASE }}
        >
          <div className="mascot__body">
            {broken ? (
              <div className="mascot__head mascot__head--stub" />
            ) : (
              <img
                className="mascot__head"
                src={headUrl}
                alt=""
                onError={() => setBroken(true)}
              />
            )}
          </div>

          <div className="mascot__ball">
            {/* Emoji değil çizim: her cihazda aynı görünsün ve dönebilsin. */}
            <svg className="mascot__ball-spin" viewBox="0 0 40 40">
              <circle cx="20" cy="20" r="19" fill="url(#mascot-ball-fill)" />
              <g fill="none" stroke="#7c2d09" strokeWidth="1.6" strokeLinecap="round">
                <path d="M20 1v38M1 20h38" />
                <path d="M6 6.5c5 4.5 7.5 8.5 7.5 13.5S11 29.5 6 34" />
                <path d="M34 6.5c-5 4.5-7.5 8.5-7.5 13.5s2.5 9.5 7.5 14" />
              </g>
              <defs>
                <radialGradient id="mascot-ball-fill" cx="34%" cy="28%" r="78%">
                  <stop offset="0%" stopColor="#ffab52" />
                  <stop offset="55%" stopColor="#e8701c" />
                  <stop offset="100%" stopColor="#b1470c" />
                </radialGradient>
              </defs>
            </svg>
          </div>

          <div className="mascot__shadow" />
        </motion.div>
      </motion.div>
    </div>
  );
}
